<script setup lang="ts">
import {
  tpgIconArrowDown,
  tpgIconDoNotDisturbOn,
} from '@tpg/grammar-web-icons';
import { useField } from 'vee-validate';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarComboBoxOptions,
  GrammarMenuItemProps,
  TpgChangeEvent,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarComboBox,
  GrammarIcon,
  GrammarText,
} from '@nora-monorepo/grammar-ui';
import type {
  GroupResultDTO,
  LocalRoleResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';
import {
  activeBgPrimary,
  AvatarImage,
  borderSeparatorNoTransparency,
  borderSeparatorTransparency,
  deselectVisibleGroups,
  deselectVisibleUsers,
  focusBgPrimary,
  getInitials,
  HoverableUserName,
  hoverBgPrimary,
  MemberSearchPicker,
  OptionsMenu,
  selectVisibleGroups,
  selectVisibleUsers,
  textSecondary,
} from '@nora-monorepo/ui-shared';

/**
 * This field component is the conversation-modal equivalent of the operation
 * member field. It binds the `members` field from vee-validate to the existing member-picker style UI, while
 * keeping all member mutations expressed through the form value.
 */
import type {
  ConversationGroupWithLocalRole,
  ConversationMembers,
  ConversationUserWithLocalRole,
} from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';

import {
  getDefaultConversationAdminRole,
  getDefaultConversationRole,
} from './conversation-modal-types';

const props = withDefaults(defineProps<{
  members: string;
  initialMembers: ConversationMembers;
  localRoles: LocalRoleResultDTO[];
  enabled?: boolean;
  readOnly?: boolean;
  isConnectedToOperation?: boolean;
}>(), {
  enabled: true,
  readOnly: false,
  isConnectedToOperation: false,
});

const { t } = useI18n();
const filterString = ref('');
const shouldShowSelectedMembersScrollHint = ref(false);
const { value, errorMessage, handleChange } = useField<ConversationMembers>(() => props.members, undefined, {
  syncVModel: false,
});
const selectedMembersListRef = useTemplateRef<HTMLElement>('selectedMembersListRef');
const selectedMembersScrollContainerRef = useTemplateRef<HTMLElement>('selectedMembersScrollContainerRef');
let selectedMembersResizeObserver: ResizeObserver | null = null;

/**
 * Normalizes the vee-validate field value so the template can always render a
 * complete members object while the form is initializing.
 */
const members = computed(() => {
  return value.value ?? { users: [], groups: [] };
});

/**
 * Resolves the default conversation role used when newly adding members from
 * the picker.
 */
function defaultRoleId(): string {
  return getDefaultConversationRole(props.localRoles).id;
}

/**
 * Resolves the local-role metadata for a rendered member so the list and role
 * sort logic can reuse the shared role definition.
 */
function getRoleForMember(
  member: ConversationUserWithLocalRole | ConversationGroupWithLocalRole,
) {
  return props.localRoles.find(localRole => localRole.id === member.role);
}

/**
 * Narrows a selected member entry to the user shape when rendering the mixed
 * user/group list.
 */
function memberIsUser(
  member: ConversationUserWithLocalRole | ConversationGroupWithLocalRole,
): member is ConversationUserWithLocalRole {
  return 'user' in member;
}

/**
 * Writes the next member selection back into the vee-validate field and marks
 * the field as changed for validation.
 */
function setMembers(nextMembers: ConversationMembers): void {
  handleChange(nextMembers, true);
}

/**
 * Looks up the original user member entry so edit-mode toggles can restore the
 * seeded role instead of defaulting to the create-mode role.
 */
function getInitialUserMember(
  userId: string,
): ConversationMembers['users'][number] | undefined {
  return props.initialMembers.users.find(member => member.user.id === userId);
}

/**
 * Looks up the original group member entry so edit-mode toggles can restore the
 * seeded role instead of defaulting to the create-mode role.
 */
function getInitialGroupMember(
  groupId: string,
): ConversationMembers['groups'][number] | undefined {
  return props.initialMembers.groups.find(member => member.group.id === groupId);
}

/**
 * Toggles a user in the current form value while preserving the non-removable
 * seeded self member.
 */
function toggleUser(memberToToggle: UserResultDTO): void {
  const existingUser = members.value.users.find(
    member => member.user.id === memberToToggle.id,
  );

  if (existingUser?.nonRemovable) {
    return;
  }

  if (existingUser) {
    setMembers({
      ...members.value,
      users: members.value.users.filter(
        member => member.user.id !== memberToToggle.id,
      ),
    });
    return;
  }

  const initialUserMember = getInitialUserMember(memberToToggle.id);

  setMembers({
    ...members.value,
    users: [
      ...members.value.users,
      initialUserMember ?? { user: memberToToggle, role: defaultRoleId() },
    ],
  });
}

/**
 * Toggles a group in the current form value.
 */
function toggleGroup(memberToToggle: GroupResultDTO): void {
  const hasGroup = members.value.groups.some(
    member => member.group.id === memberToToggle.id,
  );

  if (hasGroup) {
    setMembers({
      ...members.value,
      groups: members.value.groups.filter(
        member => member.group.id !== memberToToggle.id,
      ),
    });
    return;
  }

  const initialGroupMember = getInitialGroupMember(memberToToggle.id);

  setMembers({
    ...members.value,
    groups: [
      ...members.value.groups,
      initialGroupMember ?? { group: memberToToggle, role: defaultRoleId() },
    ],
  });
}

function onSelectAllUsers(shouldSelectAllUsers: boolean, users: UserResultDTO[]): void {
  if (shouldSelectAllUsers) {
    const nextMembers = selectVisibleUsers(
      members.value,
      users,
      defaultRoleId(),
    );

    setMembers({
      users: nextMembers.users,
      groups: nextMembers.groups,
    });
    return;
  }

  const nextMembers = deselectVisibleUsers(
    members.value,
    users,
  );

  setMembers({
    users: nextMembers.users,
    groups: nextMembers.groups,
  });
}

function onSelectAllGroups(shouldSelectAllGroups: boolean, groups: GroupResultDTO[]): void {
  if (shouldSelectAllGroups) {
    const nextMembers = selectVisibleGroups(
      members.value,
      groups,
      defaultRoleId(),
    );

    setMembers({
      users: nextMembers.users,
      groups: nextMembers.groups,
    });
    return;
  }

  const nextMembers = deselectVisibleGroups(
    members.value,
    groups,
  );

  setMembers({
    users: nextMembers.users,
    groups: nextMembers.groups,
  });
}

/**
 * Updates the selected local role for a user or group already present in the
 * member list.
 */
function updateMemberRole(memberId: string, roleId: string): void {
  setMembers({
    users: members.value.users.map((member) => {
      if (member.user.id !== memberId) {
        return member;
      }

      return {
        ...member,
        role: roleId,
      };
    }),
    groups: members.value.groups.map((member) => {
      if (member.group.id !== memberId) {
        return member;
      }

      return {
        ...member,
        role: roleId,
      };
    }),
  });
}

/**
 * Applies an inline user-role combobox selection if the control emitted a
 * concrete role id.
 */
function onUserRoleChange(userId: string, event: TpgChangeEvent<string>): void {
  const selectedRoleId = event.value;
  if (!selectedRoleId) {
    return;
  }

  updateMemberRole(userId, selectedRoleId);
}

/**
 * Applies an inline group-role combobox selection if the control emitted a
 * concrete role id.
 */
function onGroupRoleChange(groupId: string, event: TpgChangeEvent<string>): void {
  const selectedRoleId = event.value;
  if (!selectedRoleId) {
    return;
  }

  updateMemberRole(groupId, selectedRoleId);
}

/**
 * Builds the per-member overflow menu while hiding destructive actions for the
 * seeded non-removable current user.
 */
function memberMenuItems(
  member: ConversationUserWithLocalRole | ConversationGroupWithLocalRole,
): GrammarMenuItemProps[] {
  if (memberIsUser(member) && member.nonRemovable) {
    return [];
  }

  return [{
    label: t('conversationDialog.removeUser'),
    leadingIconTemplate: tpgIconDoNotDisturbOn,
    onSelect: () => {
      if (memberIsUser(member)) {
        toggleUser(member.user);
        return;
      }

      toggleGroup(member.group);
    },
  }];
}

/**
 * Sorts members by local-role hierarchy so admins remain grouped ahead of
 * lower-priority roles.
 */
function sortedRoles(
  memberA: ConversationUserWithLocalRole | ConversationGroupWithLocalRole,
  memberB: ConversationUserWithLocalRole | ConversationGroupWithLocalRole,
) {
  const roleAHierarchy = getRoleForMember(memberA)?.hierarchy ?? 0;
  const roleBHierarchy = getRoleForMember(memberB)?.hierarchy ?? 0;

  return roleBHierarchy - roleAHierarchy;
}

/**
 * Produces the rendered member list as one role-sorted collection so users and
 * groups share the same display ordering.
 */
const sortedParticipants = computed(() => {
  const allMembers = [...members.value.users, ...members.value.groups];

  return allMembers.sort(sortedRoles);
});

/**
 * Formats the selected user count label shown above the selected-members list.
 */
const numberOfSelectedMembers = computed(() => {
  return `${t('conversationDialog.labelMembers')} : (${members.value.users.length})`;
});

/**
 * Formats the selected group count label shown above the selected-members list.
 */
const numberOfSelectedGroups = computed(() => {
  return `${t('searchResults.groups')} : (${members.value.groups.length})`;
});

/**
 * Computes the currently selected admin count using the loaded conversation
 * role metadata.
 */
const numberOfAdminsInConversation = computed(() => {
  if (props.localRoles.length === 0) {
    return `${t('conversationsList.admins')} : (0)`;
  }

  const adminRoleId = getDefaultConversationAdminRole(props.localRoles).id;
  const userAdminCount = members.value.users.filter(
    member => member.role === adminRoleId,
  ).length;
  const groupAdminCount = members.value.groups.filter(
    member => member.role === adminRoleId,
  ).length;

  return `${t('conversationsList.admins')} : (${userAdminCount + groupAdminCount})`;
});

/**
 * Maps the loaded local roles into combobox options for the inline role
 * selectors.
 */
const roleOptions = computed((): GrammarComboBoxOptions[] => {
  return props.localRoles.map(localRole => ({
    label: localRole.name,
    value: localRole.id,
  }));
});

/**
 * Shows the bottom fade-arrow hint only when the selected-members list
 * overflows and there is still content below the current scroll position.
 */
function updateSelectedMembersScrollHint(): void {
  const selectedMembersScrollContainer = selectedMembersScrollContainerRef.value;
  if (!selectedMembersScrollContainer) {
    shouldShowSelectedMembersScrollHint.value = false;

    return;
  }

  const hasOverflow = selectedMembersScrollContainer.scrollHeight > selectedMembersScrollContainer.clientHeight + 1;
  const hasMoreContentBelow = selectedMembersScrollContainer.scrollTop + selectedMembersScrollContainer.clientHeight < selectedMembersScrollContainer.scrollHeight - 1;

  shouldShowSelectedMembersScrollHint.value = hasOverflow && hasMoreContentBelow;
}

onMounted(() => {
  updateSelectedMembersScrollHint();

  if ('ResizeObserver' in window) {
    selectedMembersResizeObserver = new ResizeObserver(() => {
      updateSelectedMembersScrollHint();
    });

    if (selectedMembersScrollContainerRef.value) {
      selectedMembersResizeObserver.observe(selectedMembersScrollContainerRef.value);
    }

    if (selectedMembersListRef.value) {
      selectedMembersResizeObserver.observe(selectedMembersListRef.value);
    }
  }
});

onUpdated(() => {
  updateSelectedMembersScrollHint();
});

onBeforeUnmount(() => {
  selectedMembersResizeObserver?.disconnect();
});
</script>

<template>
  <div
    class="p-2 rounded-xs h-full flex flex-row overflow-auto dark:text-dark-label-secondary text-light-label-secondary"
  >
    <div
      v-if="!isConnectedToOperation"
      class="flex-1 w-1/2 h-full flex flex-col border-r p-3"
      :class="borderSeparatorTransparency"
    >
      <MemberSearchPicker
        v-model:filter-string="filterString"
        :current-member-list="members"
        :enabled="enabled && !readOnly"
        @toggle-user="toggleUser"
        @toggle-group="toggleGroup"
        @select-all-groups="onSelectAllGroups"
        @select-all-users="onSelectAllUsers"
      />
    </div>

    <div class="p-3 w-1/2 flex-1 h-full min-h-0 flex flex-col">
      <span
        class="mr-auto text-sm font-semibold mb-2"
        :class="textSecondary"
      >
        <span> {{ numberOfSelectedMembers }}, </span>
        <span> {{ numberOfSelectedGroups }}, </span>
        <span> {{ numberOfAdminsInConversation }} </span>
      </span>

      <span
        v-if="errorMessage"
        data-testid="members-error"
        role="alert"
        class="mb-2 block text-xs text-[#ff1fd2]"
      >
        {{ errorMessage }}
      </span>

      <div class="relative flex-1 min-h-0">
        <div
          ref="selectedMembersScrollContainerRef"
          class="h-full overflow-y-auto pr-2"
          @scroll="updateSelectedMembersScrollHint"
        >
          <div
            ref="selectedMembersListRef"
            data-testid="selected-members-list"
            class="bg-light-bg-primary dark:bg-dark-bg-primary border px-3 rounded-sm"
            :class="errorMessage
              ? 'border-[var(--nora-error)] ring-1 ring-[var(--nora-error)]'
              : 'border-transparent'"
          >
            <div
              v-for="member in sortedParticipants"
              :key="memberIsUser(member) ? member.user.id : member.group.id"
              class="flex flex-row items-center w-full p-4 border-b last:border-b-0"
              :class="[
                borderSeparatorNoTransparency,
                {
                  [`${hoverBgPrimary} ${focusBgPrimary} ${activeBgPrimary}`]:
                    memberIsUser(member) && !readOnly && !isConnectedToOperation,
                },
              ]"
            >
              <AvatarImage
                :id="memberIsUser(member) ? member.user.id : member.group.id"
                class="mr-2"
                :text="memberIsUser(member) ? getInitials(member.user) : ''"
                :icon="memberIsUser(member) ? '' : 'groups'"
              />

              <HoverableUserName
                v-if="memberIsUser(member)"
                class="mx-1 flex-1"
                :user="member.user"
                :data-testid="member.nonRemovable ? 'memberPicker.currentUser' : 'memberPicker.user'"
              />
              <span v-else class="mx-1 flex-1">
                <GrammarText :text="formatName(member.group) ?? ''" />
              </span>

              <span
                v-if="readOnly || isConnectedToOperation"
                class="ml-2 mr-3 shrink-0 text-xs"
                :class="textSecondary"
              >
                {{ getRoleForMember(member)?.name }}
              </span>
              <div v-else class="w-35 mr-3 shrink-0">
                <GrammarComboBox
                  :selected-value="member.role"
                  :options="roleOptions"
                  :full-width="true"
                  :data-testid="memberIsUser(member)
                    ? (member.nonRemovable ? 'memberPicker.nonRemovableUserRole' : 'memberPicker.userRole')
                    : 'memberPicker.groupRole'"
                  @tpg-change="memberIsUser(member)
                    ? onUserRoleChange(member.user.id, $event)
                    : onGroupRoleChange(member.group.id, $event)"
                />
              </div>
              <span
                v-if="memberIsUser(member) && member.nonRemovable"
                data-testid="memberPicker.adminRole"
              />
              <div
                v-if="!readOnly && !isConnectedToOperation"
                class="self-center"
              >
                <OptionsMenu
                  v-if="memberMenuItems(member).length > 0"
                  :menu-items="memberMenuItems(member)"
                />
                <span
                  v-if="memberIsUser(member) && member.nonRemovable"
                  data-testid="memberPicker.nonRemovableUser"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="shouldShowSelectedMembersScrollHint"
          class="pointer-events-none absolute -bottom-2 right-1 z-10 flex h-12 w-12 items-center justify-center text-light-label-secondary leading-none dark:text-dark-label-secondary"
        >
          <GrammarIcon :icon-template="tpgIconArrowDown" size="xlarge" class="block" />
        </div>
      </div>
    </div>
  </div>
</template>
